// Primary-bid summary cell used by both the desktop list table and the
// mobile card. Render variants:
//   - `null` summary → muted "Awaiting bids" placeholder with subtle
//     icon. Surfaces a stable `*-primary-bid-empty-*` testid that the
//     existing list specs already key on.
//   - `summary` present → top line `price + currency`, second line
//     `carrier · Nd`. Days is omitted when the wire field is null.
//
// The component stays render-only — empty/loaded branch decisions live
// here so the host can pass either state through a single component.

import { useTranslation } from 'react-i18next';
import type { RussiaRequestPrimaryBidSummary, RussiaRequestCarrierBidUnit } from '@magimp/shared';

import { Icon } from '../../../ui/index.js';

// WHY (#1060): suffix lives in the i18n bundle so the strict-literal
//   gate keeps Cyrillic/CJK out of components. Typed key literals keep
//   the i18n bundle check exhaustive.
type UnitSuffixKey =
  | 'drawer.carriers.unitSuffix.total'
  | 'drawer.carriers.unitSuffix.kg'
  | 'drawer.carriers.unitSuffix.m3'
  | 'drawer.carriers.unitSuffix.piece';

function unitSuffixKey(unit: RussiaRequestCarrierBidUnit): UnitSuffixKey {
  if (unit === 'kg') return 'drawer.carriers.unitSuffix.kg';
  if (unit === 'm3') return 'drawer.carriers.unitSuffix.m3';
  if (unit === 'piece') return 'drawer.carriers.unitSuffix.piece';
  return 'drawer.carriers.unitSuffix.total';
}

export function RussiaRequestPrimaryBidCell({
  summary,
  rowId,
  i18nLanguage,
}: {
  summary: RussiaRequestPrimaryBidSummary | null;
  rowId: string;
  i18nLanguage: string;
}) {
  const { t } = useTranslation('russiaRequests');

  if (summary === null) {
    return (
      <span
        data-testid={`russia-requests-row-primary-bid-empty-${rowId}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          color: 'var(--fg-3)',
          fontSize: 12,
        }}
      >
        <Icon name="clock" size={12} />
        <span>{t('columns.primaryBidAwaiting')}</span>
      </span>
    );
  }

  let price: string;
  try {
    price = new Intl.NumberFormat(i18nLanguage).format(summary.price);
  } catch {
    price = String(summary.price);
  }
  return (
    <div style={{ lineHeight: 1.2 }}>
      <div
        data-testid={`russia-requests-row-primary-bid-price-${rowId}`}
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--fg-1)',
          fontSize: 12,
        }}
      >
        {price} {summary.currency}
        {t(unitSuffixKey(summary.unit))}
      </div>
      <div
        data-testid={`russia-requests-row-primary-bid-meta-${rowId}`}
        style={{ fontSize: 11, color: 'var(--fg-3)' }}
      >
        {summary.carrierName}
        {summary.daysMin !== null && summary.daysMax !== null
          ? ` · ${
              summary.daysMin === summary.daysMax
                ? t('columns.primaryBidDays', { count: summary.daysMin })
                : t('columns.primaryBidDaysRange', {
                    min: summary.daysMin,
                    max: summary.daysMax,
                  })
            }`
          : null}
      </div>
    </div>
  );
}
